import { exportAiProject, importAiProject } from './aiProjectStore'

// Local IndexedDB library of saved AI Studio projects. A project record holds
// the showcase metadata (name/description/video/images/thumbnail) plus the full
// project payload from exportAiProject (session + appModeStorage + the AI IDB
// dumps, models included), so opening a saved project restores it exactly with
// no retraining. Kept in its own database so normal-mode ProgrammingPage stores
// are never touched. Nothing leaves the machine (privacy 18.3).
const DB = 'PlodeAiProjects'
const STORE = 'projects'
const CURRENT_KEY = 'aiCurrentProjectId'

const open = () =>
    new Promise((resolve, reject) => {
        let req
        try {
            req = indexedDB.open(DB, 1)
        } catch (e) {
            reject(e)
            return
        }
        req.onupgradeneeded = () => {
            const db = req.result
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'id' })
            }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
        req.onblocked = () => reject(new Error('project database blocked'))
    })

const tx = (db, mode, run) =>
    new Promise((resolve, reject) => {
        const t = db.transaction(STORE, mode)
        const os = t.objectStore(STORE)
        let result
        run(os, (r) => {
            result = r
        })
        t.oncomplete = () => {
            db.close()
            resolve(result)
        }
        t.onerror = () => {
            db.close()
            reject(t.error)
        }
        t.onabort = () => {
            db.close()
            reject(t.error)
        }
    })

const uid = () =>
    `aiproj_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

export const getCurrentProjectId = () => sessionStorage.getItem(CURRENT_KEY) || ''
export const setCurrentProjectId = (id) => {
    if (id) sessionStorage.setItem(CURRENT_KEY, id)
    else sessionStorage.removeItem(CURRENT_KEY)
}
export const clearCurrentProjectId = () => sessionStorage.removeItem(CURRENT_KEY)

// All saved projects, newest first. Records carry their full payload, so callers
// that only need the showcase (name/thumbnail) simply ignore `data`.
export const listAiProjects = async () => {
    const db = await open()
    const all = await tx(db, 'readonly', (os, set) => {
        const rq = os.getAll()
        rq.onsuccess = () => set(rq.result || [])
    })
    return (all || []).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
}

export const getAiProject = async (id) => {
    const db = await open()
    return tx(db, 'readonly', (os, set) => {
        const rq = os.get(id)
        rq.onsuccess = () => set(rq.result || null)
    })
}

const putRecord = async (rec) => {
    const db = await open()
    await tx(db, 'readwrite', (os) => os.put(rec))
    return rec
}

// Save the current studio as a project. With an id (or a tracked current id) it
// updates that project; otherwise it creates a new one. Returns the record.
export const saveAiProjectDb = async (meta = {}) => {
    const id = meta.id || getCurrentProjectId() || uid()
    const existing = await getAiProject(id)
    const data = await exportAiProject(meta.name || existing?.name || '')
    const now = Date.now()
    const images = meta.images || existing?.images || []
    const rec = {
        id,
        kind: 'ai-studio',
        name: meta.name || existing?.name || 'Untitled project',
        description: meta.description ?? existing?.description ?? '',
        videoLink: meta.videoLink ?? existing?.videoLink ?? '',
        images,
        thumbnail: images[0] || existing?.thumbnail || '',
        data,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
    }
    await putRecord(rec)
    setCurrentProjectId(id)
    return rec
}

// Restore a saved project into the live studio state. Models come back too.
export const openAiProjectDb = async (id) => {
    const rec = await getAiProject(id)
    if (!rec || !rec.data) throw new Error('Project not found')
    await importAiProject(rec.data)
    setCurrentProjectId(id)
    return rec
}

export const deleteAiProjectDb = async (id) => {
    const db = await open()
    await tx(db, 'readwrite', (os) => os.delete(id))
    if (getCurrentProjectId() === id) clearCurrentProjectId()
}

export const renameAiProjectDb = async (id, name) => {
    const rec = await getAiProject(id)
    if (!rec) return null
    rec.name = name
    rec.updatedAt = Date.now()
    return putRecord(rec)
}

export const duplicateAiProjectDb = async (id) => {
    const rec = await getAiProject(id)
    if (!rec) return null
    const now = Date.now()
    const copy = {
        ...rec,
        id: uid(),
        name: `${rec.name} copy`,
        createdAt: now,
        updatedAt: now,
    }
    return putRecord(copy)
}
